// Blog post trust signals (organic drop fix 3, 28 Oct 2026): sources, real
// dates, a table of contents and videos.
//
// The informational blog lost about 90% of its impressions in the March and
// May 2026 Google core updates (position 6 to 19). The posts cite no sources,
// and the WordPress table of contents, whose #t- anchors gave Google its
// "jump to" results (4,417 impressions a day in Mar 2026, 132 in Oct), did
// not come across in the move.
//
// Posts are credited to RESTORE Hair, not to a physician, and claim no
// medical review (Brian, 31 Oct 2026). Sources are the ones RESTORE approved.
// An empty entry renders nothing, on the page or in the JSON-LD.
//
// Pure string work, the same on the server and in the browser, so the server
// HTML and the hydrated HTML match.

import { SITE_ORIGIN, videoUploadDateTime } from "@/lib/video-seo";
import { WP_POST_DATES } from "@/lib/wp-post-dates.generated";

export type PostSource = { title: string; url: string; publisher?: string };

// Post slug to the sources the post relies on. Rendered as a "Sources" list
// at the end of the post. Leave a post out when its body already has a
// References section: those links are read from the body instead.
//
// RESTORE approved these sources with the corrections to each post (Brian,
// 31 Oct 2026). Titles are each page's own title, except the AAD hair
// transplant page, whose title says "permanent", a word RESTORE does not use
// for results.
const AAD = "American Academy of Dermatology";
const AAD_TRANSPLANT: PostSource = {
  title: "Hair loss treatment: Hair transplant",
  url: "https://www.aad.org/public/diseases/hair-loss/treatment/transplant",
  publisher: AAD,
};
const ISHRS_FUE: PostSource = {
  title: "FUE Hair Transplant: Benefits, Process & Recovery",
  url: "https://ishrs.org/fue-what-is-it/",
  publisher: "International Society of Hair Restoration Surgery",
};
export const POST_SOURCES: Record<string, PostSource[]> = {
  "10-ways-actually-make-hair-grow": [
    {
      title:
        "Standardized Scalp Massage Results in Increased Hair Thickness by Inducing Stretching Forces to Dermal Papilla Cells in the Subcutaneous Tissue (Koyama et al., 2016)",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC4740347/",
      publisher: "Eplasty",
    },
    {
      title: "Hair loss: Tips for managing",
      url: "https://www.aad.org/public/diseases/hair-loss/treatment/tips",
      publisher: AAD,
    },
    {
      title: "Tips for healthy hair",
      url: "https://www.aad.org/public/everyday-care/hair-scalp-care/hair/healthy-hair-tips",
      publisher: AAD,
    },
  ],
  "what-is-the-norwood-scale": [
    {
      title: "Classifications of Patterned Hair Loss: A Review (Gupta and Mysore, 2016)",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC4812885/",
      publisher: "Journal of Cutaneous and Aesthetic Surgery",
    },
    {
      title: "Male pattern baldness: classification and incidence (Norwood, 1975)",
      url: "https://pubmed.ncbi.nlm.nih.gov/1188424/",
      publisher: "Southern Medical Journal",
    },
    {
      title: "Patterned loss of hair in man: types and incidence (Hamilton, 1951)",
      url: "https://pubmed.ncbi.nlm.nih.gov/14819896/",
      publisher: "Annals of the New York Academy of Sciences",
    },
  ],
  "baldness-skip-generation": [
    {
      title:
        "Genetic variation in the human androgen receptor gene is the major determinant of common early-onset androgenetic alopecia (Hillmer et al., 2005)",
      url: "https://pubmed.ncbi.nlm.nih.gov/15902657/",
      publisher: "American Journal of Human Genetics",
    },
    {
      title: "Genetic prediction of male pattern baldness (Hagenaars et al., 2017)",
      url: "https://journals.plos.org/plosgenetics/article?id=10.1371/journal.pgen.1006594",
      publisher: "PLOS Genetics",
    },
    {
      title:
        "Hair loss among elderly men: etiology and impact on perceived age (Rexbye et al., 2005)",
      url: "https://pubmed.ncbi.nlm.nih.gov/16127116/",
      publisher: "The Journals of Gerontology, Series A",
    },
  ],
  "how-quickly-does-a-hair-transplant-start-to-grow": [
    AAD_TRANSPLANT,
    ISHRS_FUE,
    {
      title: "Hair Replacement Surgery",
      url: "https://my.clevelandclinic.org/cosmetic-plastic-surgery/procedures/hair-loss",
      publisher: "Cleveland Clinic",
    },
  ],
  "dht-hair-loss": [
    {
      title: "DHT (Dihydrotestosterone): What It Is, Side Effects & Levels",
      url: "https://my.clevelandclinic.org/health/articles/24555-dht-dihydrotestosterone",
      publisher: "Cleveland Clinic",
    },
    {
      title:
        "The effects of finasteride on scalp skin and serum androgen levels in men with androgenetic alopecia (Drake et al., 1999)",
      url: "https://pubmed.ncbi.nlm.nih.gov/10495374/",
      publisher: "Journal of the American Academy of Dermatology",
    },
    {
      title: "Hair Transplantation (StatPearls)",
      url: "https://pubmed.ncbi.nlm.nih.gov/31613520/",
      publisher: "StatPearls",
    },
    {
      title:
        "Adolescent acne: association to sex, puberty, testosterone and dihydrotestosterone (Jakobsen et al., 2025)",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12002860/",
      publisher: "Endocrine Connections",
    },
    {
      title:
        "Natural Hair Supplement: Friend or Foe? Saw Palmetto, a Systematic Review in Alopecia (Evron et al., 2020)",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC7706486/",
      publisher: "Skin Appendage Disorders",
    },
  ],
  "the-true-hair-restoration-cost": [
    ISHRS_FUE,
    AAD_TRANSPLANT,
    {
      title: "Hair Transplant: How Does It Work, Success Rates",
      url: "https://my.clevelandclinic.org/health/treatments/21519-hair-transplant",
      publisher: "Cleveland Clinic",
    },
    {
      title: "What causes hair loss and what hair restoration procedures are available?",
      url: "https://www.plasticsurgery.org/news/blog/what-causes-hair-loss-and-what-hair-restoration-procedures-are-available",
      publisher: "American Society of Plastic Surgeons",
    },
  ],
};

export type VideoClip = { name: string; startOffset: number; endOffset: number };

type PostVideoBase = {
  /** Unique within the post. The player's element id is video-<key>. */
  key: string;
  name: string;
  description: string;
  /** Root-relative or absolute. Only a real poster or thumbnail; none is generated. */
  thumbnailUrl?: string;
  /** Text under the player. None when the original had no caption. */
  caption?: string;
  /** YYYY-MM-DD, or a full ISO 8601 date-time. */
  uploadDate: string;
  /** ISO 8601 duration, read from the file or the YouTube page. */
  duration: string;
  /** Shown just before the H2/H3 with this id. */
  beforeHeadingId?: string;
  /**
   * Shown just after the top-level paragraph whose text starts with this
   * (curly and straight quotes match). Used when the video sat mid-section.
   * With neither anchor, or when the anchor is not found, the video goes at
   * the end of the post.
   */
  afterParagraphStartingWith?: string;
};

export type PostVideo =
  | (PostVideoBase & {
      kind: "file";
      contentUrl: string;
      /** The file's frame size (ffprobe), so the player keeps its shape before it loads. */
      width: number;
      height: number;
      /** Key moments, in seconds. Only from the real video's timestamps. */
      clips?: VideoClip[];
    })
  | (PostVideoBase & { kind: "youtube"; youtubeId: string; thumbnailUrl: string });

/** The privacy-enhanced YouTube player. The site CSP frame-src allows this host. */
export function youtubeEmbedUrl(id: string): string {
  return `https://www.youtube-nocookie.com/embed/${id}`;
}

// Post slug to its videos, in the place each had on WordPress, with VideoObject
// JSON-LD (and hasPart Clip for a file video with clips). A Clip URL is the
// post with ?t=<start seconds>&v=<key>; that player seeks to the time
// (PostVideo in $slug.tsx).
export const POST_VIDEOS: Record<string, PostVideo[]> = {
  // The seven videos WordPress had on this post, in its order, each after the
  // paragraph it followed there. The files are RESTORE's own, on CloudFront
  // (the same objects as restorehair.s3.us-east-2.amazonaws.com, which
  // WordPress linked). WordPress gave them no poster, title or caption, and
  // they are not in the WP media library. The name and description come from
  // the WordPress section heading each one sat under (RESTORE to confirm). The
  // poster is a real frame from each file (ffmpeg at 1 s; 3 s for the first,
  // whose 1 s frame is the logo card), in public/img/video/. No caption.
  // uploadDate is the file's Last-Modified on CloudFront; duration is ffprobe
  // on the file (28 Oct 2026). "No Shave v3.mov" (245.6 MB, H.264 with PCM
  // audio) is left out until it is re-encoded: see the PR.
  "deion-sanders-hair-transplant": [
    {
      key: "the-restore-difference",
      kind: "file",
      width: 1920,
      height: 1080,
      name: "Deion Sanders hair transplant: Deion’s Accolades",
      description:
        "Video from “Deion’s Accolades” in RESTORE's Deion Sanders hair transplant article.",
      thumbnailUrl: "/img/video/deion-the-restore-difference.jpg",
      contentUrl:
        "https://d17ehmrb6zqtjv.cloudfront.net/restore_therestoredifference_v3.1+(1080p)+(1).mp4",
      uploadDate: "2025-04-09T18:20:02Z",
      duration: "PT1M",
      afterParagraphStartingWith: "Deion Sanders’ career accolades reflect his status",
    },
    {
      key: "deion-story-of-hair-loss",
      kind: "file",
      width: 1080,
      height: 1920,
      name: "Deion Sanders hair transplant: Deion Sanders' Hair Loss Journey",
      description:
        "Video from “Deion Sanders' Hair Loss Journey” in RESTORE's Deion Sanders hair transplant article.",
      thumbnailUrl: "/img/video/deion-story-of-hair-loss.jpg",
      contentUrl: "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Deion+Story+Of+Hair+Loss.mp4",
      uploadDate: "2023-11-06T20:46:37Z",
      duration: "PT21S",
      afterParagraphStartingWith: "In response to the changing landscape of his hair",
    },
    {
      key: "i-need-the-reality",
      kind: "file",
      width: 1080,
      height: 1920,
      name: "Deion Sanders hair transplant: The Decision for a Hair Transplant",
      description:
        "Video from “The Decision for a Hair Transplant” in RESTORE's Deion Sanders hair transplant article.",
      thumbnailUrl: "/img/video/deion-i-need-the-reality.jpg",
      contentUrl: "https://d17ehmrb6zqtjv.cloudfront.net/Videos/I+Need+the+Reality+(1).mp4",
      uploadDate: "2023-11-14T23:27:21Z",
      duration: "PT1M22S",
      afterParagraphStartingWith: "The decision to restore one's hair is deeply personal",
    },
    {
      key: "barber-back",
      kind: "file",
      width: 1080,
      height: 1920,
      name: "Deion Sanders hair transplant: How Did People React to Deion's New Hair?",
      description:
        "Video from “How Did People React to Deion's New Hair?” in RESTORE's Deion Sanders hair transplant article.",
      thumbnailUrl: "/img/video/deion-barber-back.jpg",
      contentUrl: "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Barber+Back.mp4",
      uploadDate: "2023-11-01T21:15:18Z",
      duration: "PT50S",
      afterParagraphStartingWith:
        "If you are interested in experiencing the transformation Deion did",
    },
    {
      key: "recommending-restore",
      kind: "file",
      width: 1080,
      height: 1920,
      name: "Deion Sanders hair transplant: What was your experience like with RESTORE Hair?",
      description:
        "Video from “What was your experience like with RESTORE Hair?” in RESTORE's Deion Sanders hair transplant article.",
      thumbnailUrl: "/img/video/deion-recommending-restore.jpg",
      contentUrl: "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Recomending+Restore.mp4",
      uploadDate: "2023-11-06T20:48:30Z",
      duration: "PT36S",
      afterParagraphStartingWith: "Deion goes on to say that RESTORE changed his life",
    },
    {
      key: "dr-harris-confidence",
      kind: "file",
      width: 1080,
      height: 1920,
      name: "Deion Sanders hair transplant: Who Is Deion Sanders Hair Doctor?",
      description:
        "Video from “Who Is Deion Sanders Hair Doctor?” in RESTORE's Deion Sanders hair transplant article.",
      thumbnailUrl: "/img/video/deion-dr-harris-confidence.jpg",
      contentUrl: "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Dr+Harris+Confidence+(1).mp4",
      uploadDate: "2023-11-14T23:32:18Z",
      duration: "PT37S",
      afterParagraphStartingWith:
        "With years of practical experience, Dr. Harris has honed his skills",
    },
  ],
  // WordPress embedded this YouTube video right before "Debunking Myths About
  // Hair Restoration Costs". Title, description, upload date and duration from
  // YouTube's oEmbed and watch page (meta itemprop), 28 Oct 2026. The
  // description stops before YouTube's last sentence, which says "permanent
  // results": RESTORE does not use "permanent" on this post. The thumbnail
  // is YouTube's maxresdefault, served from this site: the CSP img-src does not
  // allow i.ytimg.com, and nothing loads from YouTube until the visitor clicks.
  "the-true-hair-restoration-cost": [
    {
      key: "new-year-new-you",
      kind: "youtube",
      youtubeId: "qiR8fJkDUfM",
      name: "New Year, New You! | Make Hair Your Resolution | RESTORE Hair",
      description:
        "It's a New Year, and what better way to kick it off than with a new you with hair?! Make hair your New Year's Resolution this year, and get your hair back now.",
      thumbnailUrl: "/img/video/qiR8fJkDUfM.webp",
      caption: "New Year, New You! | Make Hair Your Resolution | RESTORE Hair",
      uploadDate: "2021-12-22T12:05:25-08:00",
      duration: "PT1M31S",
      beforeHeadingId: "t-1736286345290",
    },
  ],
};

const HEADING_RE = /<h2\b([^>]*)>([\s\S]*?)<\/h2>/gi;
const ID_RE = /\sid\s*=\s*(?:"([^"]*)"|'([^']*)')/i;

function decodeText(html: string): string {
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;|&#160;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;|&rsquo;|&#8217;/g, "’")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 60)
    .replace(/-$/, "");
}

export type TocItem = { id: string; text: string };

/**
 * The post's H2 headings as a table of contents, and the HTML with an id on
 * every H2. An id the post already has is kept: the WordPress #t-… anchors
 * are the URLs Google showed as "jump to" results. An H2 without one gets an
 * id from its text.
 *
 * No table of contents (an empty list) when the post has fewer than 3 H2s,
 * or when its body already links to its own headings (an in-body contents
 * list came across from WordPress on some posts).
 */
export function withTableOfContents(html: string): { html: string; items: TocItem[] } {
  const used = new Set<string>();
  for (const m of html.matchAll(/\sid\s*=\s*["']([^"']+)["']/gi)) used.add(m[1]!);
  const items: TocItem[] = [];
  const out = html.replace(HEADING_RE, (whole, attrs: string, inner: string) => {
    const text = decodeText(inner);
    if (!text) return whole;
    const existing = ID_RE.exec(attrs);
    let id = existing ? (existing[1] ?? existing[2] ?? "") : "";
    if (id) {
      items.push({ id, text });
      return whole;
    }
    const base = slugify(text) || "section";
    id = base;
    for (let n = 2; used.has(id); n += 1) id = `${base}-${n}`;
    used.add(id);
    items.push({ id, text });
    return `<h2${attrs} id="${id}">${inner}</h2>`;
  });
  const ids = new Set(items.map((i) => i.id));
  const linksOwnHeadings = [...html.matchAll(/href\s*=\s*["']#([^"']+)["']/gi)].some((m) =>
    ids.has(m[1]!),
  );
  if (items.length < 3 || linksOwnHeadings) return { html: out, items: [] };
  return { html: out, items };
}

/**
 * The links under a "References" or "Sources" H2 in the post body, for
 * BlogPosting citation. Only http(s) links off restorehair.com.
 */
export function bodyCitations(html: string): string[] {
  const m = /<h2\b[^>]*>([\s\S]*?)<\/h2>([\s\S]*?)(?=<h2\b|$)/gi;
  const urls: string[] = [];
  for (const section of html.matchAll(m)) {
    if (!/^(references|sources|citations)\b/i.test(decodeText(section[1]!))) continue;
    for (const link of section[2]!.matchAll(/href\s*=\s*["'](https?:\/\/[^"']+)["']/gi)) {
      const url = link[1]!.replace(/&amp;/g, "&");
      if (/^https?:\/\/(www\.)?restorehair\.com\//i.test(url)) continue;
      if (!urls.includes(url)) urls.push(url);
    }
  }
  return urls;
}

/** BlogPosting citation: the configured sources, else the body's References links. */
export function postCitations(slug: string, html: string): Record<string, unknown>[] | undefined {
  const configured = POST_SOURCES[slug];
  const list = configured?.length
    ? configured.map((s) => ({
        "@type": "CreativeWork",
        name: s.title,
        url: s.url,
        ...(s.publisher ? { publisher: { "@type": "Organization", name: s.publisher } } : {}),
      }))
    : bodyCitations(html).map((url) => ({ "@type": "CreativeWork", url }));
  return list.length ? list : undefined;
}

/** VideoObject JSON-LD for each of the post's videos, with hasPart Clip for key moments. */
export function postVideoObjects(slug: string): Record<string, unknown>[] {
  const pageUrl = `${SITE_ORIGIN}/${slug}/`;
  return (POST_VIDEOS[slug] ?? []).map((video) => {
    const clips =
      video.kind === "file" ? (video.clips ?? []).filter((c) => c.endOffset > c.startOffset) : [];
    const thumbnail = video.thumbnailUrl?.startsWith("/")
      ? `${SITE_ORIGIN}${video.thumbnailUrl}`
      : video.thumbnailUrl;
    return {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      name: video.name,
      description: video.description,
      ...(thumbnail ? { thumbnailUrl: [thumbnail] } : {}),
      uploadDate: videoUploadDateTime(video.uploadDate),
      duration: video.duration,
      ...(video.kind === "file"
        ? { contentUrl: video.contentUrl }
        : { embedUrl: youtubeEmbedUrl(video.youtubeId) }),
      url: pageUrl,
      ...(clips.length
        ? {
            hasPart: clips.map((c) => ({
              "@type": "Clip",
              name: c.name,
              startOffset: c.startOffset,
              endOffset: c.endOffset,
              url: `${pageUrl}?t=${c.startOffset}&v=${video.key}`,
            })),
          }
        : {}),
    };
  });
}

export type BodySegment = { html: string } | { video: PostVideo };

/**
 * The post HTML cut before each video's heading (beforeHeadingId), with the
 * video between the pieces. A video whose heading is not in the HTML goes at
 * the end, so a renamed heading never drops a video.
 */
export function placeVideos(html: string, videos: PostVideo[]): BodySegment[] {
  const placed: { at: number; video: PostVideo }[] = [];
  const trailing: PostVideo[] = [];
  const paragraphs = videos.some((v) => v.afterParagraphStartingWith)
    ? topLevelParagraphs(html)
    : [];
  for (const video of videos) {
    let at = -1;
    if (video.beforeHeadingId) {
      const id = video.beforeHeadingId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const m = new RegExp(`<h[2-6]\\b[^>]*\\sid\\s*=\\s*["']${id}["']`, "i").exec(html);
      if (m) at = m.index;
    } else if (video.afterParagraphStartingWith) {
      const want = comparable(video.afterParagraphStartingWith);
      const p = paragraphs.find((para) => para.text.startsWith(want));
      if (p) at = p.end;
    }
    if (at >= 0) placed.push({ at, video });
    else trailing.push(video);
  }
  placed.sort((a, b) => a.at - b.at);
  const segments: BodySegment[] = [];
  let from = 0;
  for (const { at, video } of placed) {
    if (at > from) segments.push({ html: html.slice(from, at) });
    segments.push({ video });
    from = at;
  }
  if (from < html.length) segments.push({ html: html.slice(from) });
  for (const video of trailing) segments.push({ video });
  return segments;
}

/** Text for matching: tags and entities removed, curly quotes made straight. */
function comparable(html: string): string {
  return decodeText(html).replace(/[‘’]/g, "'").replace(/[“”]/g, '"');
}

const VOID_TAGS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "source",
  "track",
  "wbr",
]);
const TAG_RE = /<(\/?)([a-zA-Z][a-zA-Z0-9-]*)\b(?:[^>"']|"[^"]*"|'[^']*')*?(\/?)>/g;

/**
 * Each <p> at nesting depth 0, with the offset just after its </p>. Cutting
 * the HTML there never splits an open element, so the server HTML and the
 * hydrated DOM match. Returns none when the tags do not balance.
 */
function topLevelParagraphs(html: string): { end: number; text: string }[] {
  const out: { end: number; text: string }[] = [];
  let depth = 0;
  let pStart = -1;
  TAG_RE.lastIndex = 0;
  for (let m = TAG_RE.exec(html); m; m = TAG_RE.exec(html)) {
    const tag = m[2]!.toLowerCase();
    if (m[1] === "/") {
      if (depth === 0) return [];
      depth -= 1;
      if (tag === "p" && depth === 0 && pStart >= 0) {
        const end = m.index + m[0].length;
        out.push({ end, text: comparable(html.slice(pStart, end)) });
        pStart = -1;
      }
      continue;
    }
    if (m[3] === "/" || VOID_TAGS.has(tag)) continue;
    if (tag === "p" && depth === 0) pStart = m.index;
    depth += 1;
  }
  return out;
}

const DAY_MS = 24 * 60 * 60 * 1000;

// Every published post's updated_at on 28 Oct 2026 fell on 8, 9, 10, 21 or
// 23 Sep 2026: the WordPress import and the move to the new site, not edits.
// A date before this is not a content update.
export const MIGRATION_ENDED_AT = "2026-09-24T00:00:00Z";

// WordPress dates are local time; the site's gmt_offset is a fixed -5.
const WP_OFFSET = "-05:00";

// 77 posts have a WordPress modified date in Aug 2026, which looks like a bulk
// plugin or SEO touch rather than an edit. It still dates the post in the
// JSON-LD and the sitemap, but the page shows no "Updated" for it.
const BULK_TOUCH_MONTH = "2026-08";

export type PostDates = {
  /** datePublished, and the visible "Published" date. */
  published: string | null;
  /** The visible "Updated" date, or null for none. */
  updated: string | null;
  /** JSON-LD dateModified and sitemap <lastmod>. */
  modified: string | null;
};

/**
 * A post's dates. No import date is ever used.
 *
 * - Published: the WordPress publish date (the Supabase published_at is a day
 *   late on 24 posts and missing on some), else published_at.
 * - Modified, in order: a blog editor save after the move, then the
 *   WordPress modified date, then Published.
 *   An editor save counts only when it is at least a day after publishing and
 *   the post is not rendered from its WordPress copy (wp_parity), whose page
 *   does not show editor saves.
 * - Updated (visible): a post-move save always shows. The WordPress
 *   modified date shows unless it is in Aug 2026 (BULK_TOUCH_MONTH).
 */
export function postDates(opts: {
  slug: string;
  publishedAt: string | null | undefined;
  updatedAt: string | null | undefined;
  wpParity?: boolean;
}): PostDates {
  const wp = WP_POST_DATES[opts.slug];
  const published = wp ? `${wp.published}${WP_OFFSET}` : (opts.publishedAt ?? null);
  const publishedMs = published ? Date.parse(published) : NaN;
  const afterPublishing = (ms: number) => Number.isNaN(publishedMs) || ms - publishedMs >= DAY_MS;

  let edit: string | null = null;
  if (opts.updatedAt && !opts.wpParity) {
    const u = Date.parse(opts.updatedAt);
    if (!Number.isNaN(u) && u >= Date.parse(MIGRATION_ENDED_AT) && afterPublishing(u)) {
      edit = opts.updatedAt;
    }
  }
  const wpModified =
    wp && afterPublishing(Date.parse(`${wp.modified}${WP_OFFSET}`))
      ? `${wp.modified}${WP_OFFSET}`
      : null;
  const wpModifiedShown =
    wpModified && !wp!.modified.startsWith(BULK_TOUCH_MONTH) ? wpModified : null;

  return {
    published,
    updated: edit ?? wpModifiedShown,
    modified: edit ?? wpModified ?? published,
  };
}

/**
 * The calendar date of an ISO 8601 value in its own offset, so a WordPress
 * "2019-05-21T20:39:15-05:00" reads May 21, not May 22 (its UTC date).
 */
export function localCalendarDate(value: string): string {
  return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : value;
}

/** A blog post's sitemap <lastmod>: the same date as its JSON-LD dateModified. */
export function blogPostLastmod(post: {
  slug: string;
  published_at: string | null;
  updated_at: string | null;
  wpParity: boolean;
}): string | null {
  return postDates({
    slug: post.slug,
    publishedAt: post.published_at,
    updatedAt: post.updated_at,
    wpParity: post.wpParity,
  }).modified;
}
